import React, { useRef, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  downloadRelatorioCsvConsultoresResumo,
  downloadRelatorioXlsxConsultoresResumo,
} from '../../../../api/contac/contacReportsConsultoresApi';
import FullScreenLoader from '../../../loading/FullScreenLoader';
import './ModalReports.css';
import '../filtro/ModalFiltro.css';
import '../../../../shared/ui/modal-close-button.css'; // X padrão do sistema

const EMPTY: string[] = [];

interface ModalReportsConsultorProps {
  isOpen: boolean;
  onClose: () => void;
  initialConsultores?: string[];
  dataInicio: string;
  dataFim: string;
}

const ModalReportsConsultor: React.FC<ModalReportsConsultorProps> = ({
  isOpen,
  onClose,
  initialConsultores,
  dataInicio,
  dataFim,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  const safeInitial = initialConsultores ?? EMPTY;
  const initialKey = safeInitial.join('|'); // deps estável mesmo se vier novo array

  const [consultores, setConsultores] = useState<string[]>(safeInitial);
  const [consultorNome, setConsultorNome] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) onClose();
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    setConsultores(safeInitial);
    setConsultorNome('');
    // A dependência proposital é o initialKey (conteúdo da lista), não o array safeInitial:
    // o pai pode entregar um array novo a cada render e depender da referência faria o efeito
    // re-executar sem parar (setConsultores -> render -> novo array -> efeito), em loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialKey]);

  if (!isOpen) return null;

  const formatarPeriodoCurto = (data: string) => {
    const meses = ['jan','fev','mar','abr','mai','jun','jul','ago','set','out','nov','dez'];
    const [ano, mes] = data.split('-');
    return `${meses[Number(mes) - 1]}/${ano}`;
  };

  const handleDownload = async () => {
    try {
      setLoading(true);

      await downloadRelatorioCsvConsultoresResumo({
        dataInicio,
        dataFim,
        consultores: consultores.length > 0 ? consultores : undefined,
        consultor: consultorNome.trim() ? consultorNome.trim() : undefined,
      });

      toast.success('Relatório CSV gerado com sucesso!');
      onClose();
    } catch {
      toast.error('Erro ao gerar CSV');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      setLoading(true);

      await downloadRelatorioXlsxConsultoresResumo({
        dataInicio,
        dataFim,
        consultores: consultores.length > 0 ? consultores : undefined,
        consultor: consultorNome.trim() ? consultorNome.trim() : undefined,
      });

      toast.success('Relatório XLSX gerado com sucesso!');
      onClose();
    } catch {
      toast.error('Erro ao gerar XLSX');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-filtro-overlay">
        <div className="modal-filtro-content" ref={modalRef}>
          <div className="filtro-modal-card">
            
            {/* Header */}
            <div className="filtro-modal-header">
              <div>
                <h2 className="filtro-modal-title">Gerar Relatório de Consultores (Resumo)</h2>
                <p className="filtro-modal-subtitle">
                  {formatarPeriodoCurto(dataInicio)} – {formatarPeriodoCurto(dataFim)}
                </p>
              </div>
              <button className="close-button" onClick={onClose} disabled={loading}>&times;</button>
            </div>

            {/* Body */}
            <div className="filtro-modal-body">
              <div className="filtro-section">
                <h3 className="filtro-section-title">Filtrar por nome (opcional)</h3>
                
                <div className="filtro-field">
                  <label>Consultor</label>
                  <input
                    type="text"
                    className="input-filtro"
                    placeholder="Ex: Jorge"
                    value={consultorNome}
                    onChange={(e) => setConsultorNome(e.target.value)}
                    disabled={loading}
                    autoFocus
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="filtro-modal-footer">
              <button
                type="button"
                className="filtro-btn-secondary"
                onClick={onClose}
                disabled={loading}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={handleDownload}
                disabled={loading}
              >
                {loading ? 'Gerando arquivo...' : 'Gerar CSV'}
              </button>
              <button
                type="button"
                className="filtro-btn-primary"
                onClick={handleDownloadXlsx}
                disabled={loading}
              >
                {loading ? 'Gerando arquivo...' : 'Gerar XLSX'}
              </button>
            </div>

          </div>
        </div>
      </div>

      {loading && <FullScreenLoader />}
    </>
  );
};

export default ModalReportsConsultor;
